import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminWebhook } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { absoluteTime, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/webhooks")({
  component: WebhooksScreen,
});

/**
 * Customer endpoints, worst first.
 *
 * A customer's endpoint failing is a support ticket nobody could answer. The
 * delivery history existed and was readable only from that customer's own
 * screen, so "are you sure you are receiving them" was a question support had
 * to ask rather than answer.
 */
function WebhooksScreen() {
  const webhooks = useQuery({
    queryKey: ["platform-webhooks"],
    queryFn: ({ signal }) =>
      api.get<{ webhooks: AdminWebhook[] | null }>("/admin/webhooks", { limit: 300 }, signal),
    refetchInterval: 60000,
  });

  const list = webhooks.data?.webhooks ?? [];
  const failing = list.filter((w) => w.failed > 0);
  const disabled = list.filter((w) => !w.active);

  return (
    <div className="flex flex-col gap-4">
      {(failing.length > 0 || disabled.length > 0) && (
        <div className="rounded-xl border border-[color-mix(in_oklab,_var(--warning)_35%,_transparent)] bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--warning)]">
            {failing.length > 0 &&
              `${failing.length} ${failing.length === 1 ? "endpoint is" : "endpoints are"} failing`}
            {failing.length > 0 && disabled.length > 0 && " · "}
            {disabled.length > 0 && `${disabled.length} switched off`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            Each of these is a customer not being told about things that happened in their account.
          </p>
        </div>
      )}

      <Panel
        padded={false}
      >
        {webhooks.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={4} columns={6} />
          </div>
        ) : webhooks.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={webhooks.error} onRetry={() => void webhooks.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No webhooks" description="Nobody has added an endpoint." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[30%]">Endpoint</Th>
                  <Th width="w-[17%]">Tenant</Th>
                  <Th width="w-[9%]" numeric>
                    Delivered
                  </Th>
                  <Th width="w-[8%]" numeric>
                    Failed
                  </Th>
                  <Th width="w-[23%]">Last failure</Th>
                  <Th width="w-[13%]" align="right">
                    Last delivery
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((w) => (
                  <Tr
                    key={w.id}
                    className={
                      !w.active || w.failed > 0
                        ? "bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]"
                        : undefined
                    }
                  >
                    <Td clip title={`${w.url} · ${w.events.join(", ")}`}>
                      {!w.active && (
                        <Chip tone="down" className="mr-1.5">
                          off
                        </Chip>
                      )}
                      <span className="mono text-[13px]">{w.url}</span>
                    </Td>
                    <Td clip title={w.organisation_name}>
                      <Link
                        to="/admin/$orgId"
                        params={{ orgId: w.organisation_id }}
                        className="hover:text-[var(--primary)]"
                      >
                        {w.organisation_name}
                      </Link>
                    </Td>
                    <Td numeric>{w.delivered}</Td>
                    <Td numeric>
                      {w.failed > 0 ? (
                        <span className="text-[var(--danger)]">{w.failed}</span>
                      ) : (
                        "—"
                      )}
                    </Td>
                    {/* The code and the body together. A customer told only
                        "delivery failed" learns nothing; "502, upstream
                        connect error" is a conversation they can act on. */}
                    <Td clip title={w.disabled_reason || w.last_failure_note || undefined}>
                      {w.last_failure_code ? (
                        <span className="text-[var(--text-secondary)]">
                          {w.last_failure_code} {w.last_failure_note || ""}
                        </span>
                      ) : w.disabled_reason ? (
                        <span className="text-[var(--text-secondary)]">{w.disabled_reason}</span>
                      ) : (
                        <span className="text-[var(--text-muted)]">—</span>
                      )}
                    </Td>
                    <Td
                      align="right"
                      title={w.last_delivered_at ? absoluteTime(w.last_delivered_at) : undefined}
                    >
                      {w.last_delivered_at ? shortTime(w.last_delivered_at) : "never"}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}
